import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { AscensionLead } from "../../hooks/useOperationsAnalytics";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// ASCENSIONS MODAL
// =============================================================================
// PURPOSE: Low→high ticket upgrade tracker showing leads who purchased low ticket first, then upgraded.
//
// REQUIREMENTS:
// - Table of leads with low ticket date/amount and high ticket date/amount
// - Timezone-aware date formatting
// - Click outside or ESC to close
// =============================================================================

interface AscensionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  leads: AscensionLead[];
  timezone?: string;
}

const formatDate = (d: string, timezone: string) => {
  if (!d) return "--";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  });
};

const AscensionsModal: React.FC<AscensionsModalProps> = ({
  isOpen,
  onClose,
  leads,
  timezone = "UTC",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    const modal = modalRef.current;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    modal.addEventListener("keydown", handleTab);
    first?.focus();
    return () => modal.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ascensions-modal-title"
        className="detail-overlay rounded-lg shadow-2xl w-full max-w-5xl m-4 p-6 relative animate-fade-in-up border border-black/[0.08] max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6 border-b border-black/[0.08] pb-3">
          <div>
            <h2 id="ascensions-modal-title" className="text-xl font-bold text-glass">
              Ascended Leads
            </h2>
            <p className="text-sm text-glass-muted mt-1">
              Leads who purchased low ticket first, then upgraded to high ticket ({leads.length}{" "}
              total)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-glass-muted hover:text-glass-secondary transition-colors"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {leads.length === 0 ? (
          <div className="text-center py-12 text-glass-muted">
            No ascended leads found in this date range.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-black/[0.08] text-left text-xs uppercase text-glass-muted tracking-wider">
                  <th className="px-4 py-3">Lead</th>
                  <th className="px-4 py-3">Low Ticket</th>
                  <th className="px-4 py-3">LT Date</th>
                  <th className="px-4 py-3">LT Paid</th>
                  <th className="px-4 py-3">Upsell Program</th>
                  <th className="px-4 py-3">Upsell Date</th>
                  <th className="px-4 py-3">Upsell Revenue</th>
                  <th className="px-4 py-3">Cash Paid</th>
                  <th className="px-4 py-3">Installments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/[0.08]">
                {leads.map((lead, i) => (
                  <tr
                    key={`${lead.leadEmail}-${i}`}
                    className="anim-row anim-pop hover:bg-black/[0.04] transition-colors"
                    style={{
                      animationDelay: `${Math.min(i, 15) * 100}ms`,
                      animationDuration: "480ms",
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-glass">{lead.leadName}</div>
                      <div className="text-xs text-glass-muted">{lead.leadEmail}</div>
                    </td>
                    <td className="px-4 py-3 text-glass-secondary">{lead.lowTicketProduct}</td>
                    <td className="px-4 py-3 text-glass-muted whitespace-nowrap">
                      {formatDate(lead.lowTicketDate, timezone)}
                    </td>
                    <td className="px-4 py-3 text-glass-secondary">
                      {formatUSD(lead.lowTicketCash)}
                    </td>
                    <td className="px-4 py-3 font-medium text-glass">{lead.upsellProduct}</td>
                    <td className="px-4 py-3 text-glass-muted whitespace-nowrap">
                      {formatDate(lead.upsellDate, timezone)}
                    </td>
                    <td className="px-4 py-3 font-medium text-glass">
                      {formatUSD(lead.upsellRevenue)}
                    </td>
                    <td className="px-4 py-3 text-ds-green font-medium">
                      {formatUSD(lead.upsellCash)}
                    </td>
                    <td className="px-4 py-3 text-blue-400 font-medium">
                      {lead.totalInstallmentsPaid > 0
                        ? formatUSD(lead.totalInstallmentsPaid)
                        : "--"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AscensionsModal;
